import { IconInfoCircle } from "@tabler/icons-react"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { FormControl, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { renderLiquid } from "lopermut"
import { FlaskConical, PlusIcon, XIcon } from "lucide-react"
import { useTestWebhookMutation } from "modules/settings/hooks/zendesk/useTestWebhookMutation"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { useVaultPreviewContext } from "modules/shared/hooks/useVaultPreviewContext"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  CAMPAIGN_ACTION_WEBHOOK_DEFAULT_PAYLOAD,
  WorkflowActionWebhookConfigSchema,
  WorkflowNodeSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useEventPayload } from "../../hooks/useEventPayload"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"
import { EditWebhookPayloadDialog } from "./EditWebhookPayloadDialog"

type WebhookConfigurationProps = {
  nodeIndex: number
  node: Extract<z.infer<typeof WorkflowNodeSchema>, { type: "Action" }>
}

type VariableOption = { value: string; label: string }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function extractVariablePaths(
  value: unknown,
  {
    maxDepth,
    maxArrayElements,
    excludePrefixes,
  }: {
    maxDepth: number
    maxArrayElements: number
    excludePrefixes: string[]
  },
) {
  const results = new Set<string>()

  function isExcludedPath(path: string) {
    return excludePrefixes.some((prefix) => path === prefix || path.startsWith(prefix))
  }

  function walk(current: unknown, path: string, depth: number) {
    if (depth > maxDepth) return
    if (path !== "" && isExcludedPath(path)) return

    if (current === null) {
      if (path !== "") results.add(path)
      return
    }

    if (
      typeof current === "string" ||
      typeof current === "number" ||
      typeof current === "boolean"
    ) {
      if (path !== "") results.add(path)
      return
    }

    if (Array.isArray(current)) {
      const len = Math.min(current.length, maxArrayElements)
      for (let i = 0; i < len; i++) {
        walk(current[i], `${path}[${i}]`, depth + 1)
      }
      return
    }

    if (isRecord(current)) {
      for (const [key, next] of Object.entries(current)) {
        const nextPath = path ? `${path}.${key}` : key
        walk(next, nextPath, depth + 1)
      }
    }
  }

  walk(value, "", 0)
  return results
}

export function WebhookConfiguration({ nodeIndex, node }: WebhookConfigurationProps) {
  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()

  const { eventPayload, eventName } = useEventPayload({ nodeId: node.id })
  const { mutateAsync: testWebhook, isPending: isTestWebhookPending } = useTestWebhookMutation()

  const liquidContext = useMemo(() => {
    if (!eventPayload) return {}

    const baseLiquidContext: Record<string, unknown> = {
      ...eventPayload,
      ...eventPayload.data,
    }

    const baseConversation = baseLiquidContext.conversation
    const baseCustomer = baseLiquidContext.customer

    return isRecord(baseConversation) && isRecord(baseCustomer)
      ? {
          ...baseLiquidContext,
          conversation: {
            ...baseConversation,
            customer: baseCustomer,
          },
        }
      : baseLiquidContext
  }, [eventPayload])
  const previewContext = useVaultPreviewContext(liquidContext)

  const urlVariables: VariableOption[] = useMemo(() => {
    if (!eventPayload) return []

    const paths = extractVariablePaths(liquidContext, {
      maxDepth: 6,
      maxArrayElements: 2,
      excludePrefixes: ["data"],
    })

    return [...paths]
      .filter((p) => p !== "")
      .sort((a, b) => a.localeCompare(b))
      .map((p) => ({ value: p, label: p }))
  }, [eventPayload, liquidContext])

  async function handleTestWebhook() {
    const formConfig = form.getValues(`nodes.${nodeIndex}.config`)
    const result = WorkflowActionWebhookConfigSchema.safeParse(formConfig)

    if (!result.success) return

    const { url, headers, payload } = result.data

    const response = await testWebhook({
      url,
      headers,
      payload: payload ?? "null",
      context: liquidContext,
    })

    if (response.success) {
      toast.success(t("campaigns.action.availableActions.SendWebhook.test.success"))
    } else {
      toast.error(t("campaigns.action.availableActions.SendWebhook.test.error"))
    }
  }

  // Validate and initialize config using zod
  useEffect(() => {
    const result = WorkflowActionWebhookConfigSchema.safeParse(node.config)

    if (!result.success) {
      form.setValue(`nodes.${nodeIndex}.config`, {
        url: "",
        headers: [],
        payload: CAMPAIGN_ACTION_WEBHOOK_DEFAULT_PAYLOAD,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Watch form values for real-time updates
  const url = form.watch(`nodes.${nodeIndex}.config.url`) || ""
  const headers = form.watch(`nodes.${nodeIndex}.config.headers`) || []
  const payload = form.watch(`nodes.${nodeIndex}.config.payload`)

  let renderedPayload: unknown | null = null
  let renderedPayloadIsValid = true
  try {
    renderedPayload = JSON.parse(
      renderLiquid(payload.trim() === "" ? "null" : payload.trim(), previewContext, {
        jsonSafe: true,
      }),
    )
  } catch {
    renderedPayloadIsValid = false
  }

  if (!eventName || !eventPayload) {
    return (
      <span className="text-sm text-muted-foreground">
        {t("campaigns.action.availableActions.SendWebhook.noEventSelected")}
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <span className="text-sm text-muted-foreground">
        {t("campaigns.action.availableActions.SendWebhook.config.description")}
      </span>
      <Button
        variant="testing"
        onClick={handleTestWebhook}
        disabled={isTestWebhookPending || !form.formState.isValid}
        className="w-fit"
      >
        <FlaskConical className="size-4" />
        <span>{t("campaigns.action.availableActions.SendWebhook.test.title")}</span>
      </Button>
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.url`}
        render={() => (
          <FormItem>
            <Label>{t("campaigns.action.availableActions.SendWebhook.config.url")}</Label>
            <FormControl>
              <VaultVariableAutocomplete variables={urlVariables} disabled={!eventPayload}>
                <Input
                  placeholder={t(
                    "campaigns.action.availableActions.SendWebhook.config.urlPlaceholder",
                  )}
                  value={url}
                  onChange={(e) => {
                    form.setValue(`nodes.${nodeIndex}.config.url`, e.target.value, {
                      shouldDirty: true,
                    })
                    form.trigger(`nodes.${nodeIndex}.config`)
                  }}
                  type="url"
                  autoComplete="off"
                />
              </VaultVariableAutocomplete>
            </FormControl>
          </FormItem>
        )}
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label>{t("campaigns.action.availableActions.SendWebhook.config.headers")}</Label>
          <div className="w-full flex items-end gap-2">
            <div className="flex flex-col gap-2 grow">
              <Label>{t("campaigns.action.availableActions.SendWebhook.config.headerKey")}</Label>
              <Input
                value="Content-Type"
                placeholder={t(
                  "campaigns.action.availableActions.SendWebhook.config.keyPlaceholder",
                )}
                readOnly
              />
            </div>
            <div className="flex flex-col gap-2 grow">
              <Label>{t("campaigns.action.availableActions.SendWebhook.config.headerValue")}</Label>
              <Input
                value="application/json"
                placeholder={t(
                  "campaigns.action.availableActions.SendWebhook.config.headerValuePlaceholder",
                )}
                readOnly
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon">
                  <IconInfoCircle className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("campaigns.action.availableActions.SendWebhook.config.defaultHeaderInfo")}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          {headers.map((header: { key: string; value: string }, index: number) => (
            <FormField
              key={index}
              control={form.control}
              name={`nodes.${nodeIndex}.config.headers.${index}`}
              render={() => (
                <FormItem className="flex items-end gap-2">
                  <div className="flex flex-col gap-2 grow">
                    <Input
                      value={header.key}
                      onChange={(e) => {
                        const newHeaders = [...headers]
                        newHeaders[index] && (newHeaders[index].key = e.target.value)
                        form.setValue(`nodes.${nodeIndex}.config.headers`, newHeaders, {
                          shouldDirty: true,
                        })
                        form.trigger(`nodes.${nodeIndex}.config`)
                      }}
                      placeholder={t(
                        "campaigns.action.availableActions.SendWebhook.config.keyPlaceholder",
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-2 grow">
                    <VaultVariableAutocomplete variables={[]}>
                      <Input
                        value={header.value}
                        onChange={(e) => {
                          const newHeaders = [...headers]
                          newHeaders[index] && (newHeaders[index].value = e.target.value)
                          form.setValue(`nodes.${nodeIndex}.config.headers`, newHeaders, {
                            shouldDirty: true,
                          })
                          form.trigger(`nodes.${nodeIndex}.config`)
                        }}
                        placeholder={t(
                          "campaigns.action.availableActions.SendWebhook.config.headerValuePlaceholder",
                        )}
                      />
                    </VaultVariableAutocomplete>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const newHeaders = [...headers]
                      newHeaders.splice(index, 1)
                      form.setValue(`nodes.${nodeIndex}.config.headers`, newHeaders, {
                        shouldDirty: true,
                      })
                    }}
                  >
                    <XIcon className="size-4" />
                  </Button>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              form.setValue(
                `nodes.${nodeIndex}.config.headers`,
                [...headers, { key: "", value: "" }],
                {
                  shouldDirty: true,
                },
              )
            }}
            className="w-fit"
          >
            <PlusIcon className="size-4" />
            {t("campaigns.action.availableActions.SendWebhook.config.addHeader")}
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label>{t("campaigns.action.availableActions.SendWebhook.config.payload")}</Label>
        <EditWebhookPayloadDialog
          initialPayload={payload}
          onChangePayload={(payload) => {
            form.setValue(`nodes.${nodeIndex}.config.payload`, payload, {
              shouldDirty: true,
            })
            form.trigger(`nodes.${nodeIndex}.config`)
          }}
          eventPayload={eventPayload}
          eventName={eventName}
          nodeIndex={nodeIndex}
        />
        <MarkdownRenderer className="bg-muted rounded-md overflow-auto">
          {`\`\`\`json\n${
            renderedPayloadIsValid
              ? JSON.stringify(renderedPayload, null, 2)
              : "The payload is invalid. Please check the syntax."
          }\n\`\`\``}
        </MarkdownRenderer>
      </div>
    </div>
  )
}
